/**
 * Single log line renderer for dev log viewers.
 */
import type { SessionLogEntry } from "@memes/shared";
import { formatLogSummary } from "../../lib/logFormat";

const LEVEL_CLASS: Record<string, string> = {
  fatal: "text-red-700 dark:text-red-300",
  error: "text-red-600 dark:text-red-400",
  warn: "text-amber-600 dark:text-amber-400",
  info: "text-gray-700 dark:text-gray-300",
  debug: "text-gray-500 dark:text-gray-500",
  trace: "text-gray-400 dark:text-gray-600",
};

function formatTime(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString(undefined, {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  } catch {
    return ts;
  }
}

export function LogLine({
  entry,
  id,
  selected = false,
  onSelect,
}: {
  entry: SessionLogEntry;
  id?: string;
  selected?: boolean;
  onSelect?: (entry: SessionLogEntry) => void;
}): React.ReactElement {
  const summary = formatLogSummary(entry);
  const showOriginalMsg =
    entry.msg && entry.msg !== summary && entry.msg !== "incoming request" && entry.msg !== "request completed";

  return (
    <button
      type="button"
      id={id}
      data-log-level={entry.level}
      onClick={() => onSelect?.(entry)}
      className={`w-full border-b border-gray-100 px-2 py-1 text-left font-mono text-[11px] leading-relaxed transition-colors dark:border-gray-800 ${
        selected
          ? "bg-indigo-100/80 dark:bg-indigo-950/50"
          : "hover:bg-gray-50 dark:hover:bg-gray-900/50"
      }`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="shrink-0 text-gray-400 tabular-nums">{formatTime(entry.ts)}</span>
        <span className={`shrink-0 uppercase ${LEVEL_CLASS[entry.level] ?? ""}`}>
          {entry.level}
        </span>
        <span className="shrink-0 text-indigo-600 dark:text-indigo-400">
          [{entry.source}]
        </span>
        {entry.module ? (
          <span className="shrink-0 text-purple-600 dark:text-purple-400">{entry.module}</span>
        ) : null}
        <span className="min-w-0 flex-1 break-words text-gray-800 dark:text-gray-200">
          {summary}
        </span>
        {showOriginalMsg ? (
          <span className="shrink-0 text-[10px] text-gray-400">({entry.msg})</span>
        ) : null}
      </div>
    </button>
  );
}
